'use client'

import type { ButtonProps } from '@pearlconvert/ui/components/button/button.types'

import React, { useCallback } from 'react'

import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { type EventPayloadType, EventType } from '@maestro/data-layer'
import { Button } from '@pearlconvert/ui/components/button'

export interface TrackingButtonProps extends ButtonProps {
  location: string
  /**
   * Explicitly sets click_url in the CTA_CLICKED event payload.
   * Defaults to the button's `url` prop when omitted.
   * Use this when the button is an action button (no url) but you still
   * want to track a specific URL (e.g. a search results link).
   */
  clickUrl?: string
}

function extractClickText(children: React.ReactNode): string {
  if (typeof children === 'string') {
    return children
  }
  if (typeof children === 'number') {
    return String(children)
  }
  if (typeof children === 'boolean' || children == null) {
    return ''
  }
  if (Array.isArray(children)) {
    return children
      .map(extractClickText)
      .filter(Boolean)
      .join(' ')
      .trim()
  }
  if (React.isValidElement(children)) {
    return extractClickText(
      (children.props as { children?: React.ReactNode }).children
    )
  }
  return ''
}

export default function TrackingButton({
  location,
  clickUrl,
  onClick,
  children,
  title,
  ariaLabel,
  url,
  ...rest
}: TrackingButtonProps) {
  const { publish } = useEventBusContext()

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const clickText =
        extractClickText(children) || title || ariaLabel || ''
      const resolvedClickUrl = clickUrl ?? url

      const payload: EventPayloadType[EventType.CTA_CLICKED] = {
        location,
        click_text: clickText,
        ...(resolvedClickUrl && { click_url: resolvedClickUrl }),
      }

      publish<EventPayloadType[EventType.CTA_CLICKED]>(
        EventType.CTA_CLICKED,
        payload
      )
      onClick?.(e)
    },
    [ariaLabel, children, clickUrl, location, onClick, publish, title, url]
  )

  return (
    <Button onClick={handleClick} url={url} {...rest}>
      {children}
    </Button>
  )
}
